import React from 'react';
import { Box, TextField, Typography } from '@mui/material';

interface ValidezBlockProps {
  // Valor mostrado (persistido o sugerencia hoy+15), "YYYY-MM-DD" o null.
  validez: string | null;
  // Sugerencia autoritativa del backend (hoy+15). Ancla el rango del selector.
  validezSugerida: string | null;
  soloLectura: boolean;
  guardando: boolean;
  error: string | null;
  onCambiar: (fecha: string) => void;
  // SPEC-171 — cuando es true neutraliza el mt superior para top-alinear dentro de la fila
  // entrega/validez; el contenedor grid aporta su propia separación superior.
  sinMargenSuperior?: boolean;
}

// SPEC-167 (RN-004/CA-009) — deriva min=hoy+1 y max=hoy+15 desde la sugerencia (hoy+15),
// sin recalcular "hoy" en el cliente (evita divergencia de zona horaria). UTC-safe.
function sumarDiasISO(iso: string, dias: number): string {
  const base = new Date(`${iso}T00:00:00.000Z`);
  const r = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate() + dias));
  return r.toISOString().slice(0, 10);
}

// SPEC-167 (TASK-008) — bloque inline "Fecha de validez" en el encabezado del Detalle.
// Date input con rango acotado (hoy, hoy+15], auto-guardado al cambio y solo lectura en
// estados no editables (RN-008). Patrón visual de SitioEntregaBlock (SPEC-159).
function ValidezBlock({
  validez,
  validezSugerida,
  soloLectura,
  guardando,
  error,
  onCambiar,
  sinMargenSuperior = false,
}: ValidezBlockProps) {
  const tituloSx = { fontSize: 13, fontWeight: 700, color: '#7C3E8E', letterSpacing: 0.3 } as const;
  const mtSuperior = sinMargenSuperior ? 0 : 1.5;

  const max = validezSugerida ?? undefined; // hoy+15
  const min = validezSugerida ? sumarDiasISO(validezSugerida, -14) : undefined; // hoy+1
  const valorMostrado = validez ?? validezSugerida ?? '';

  if (soloLectura) {
    return (
      <Box
        data-testid="validez-block"
        sx={{ mt: mtSuperior, p: 1.5, background: '#FBF7FC', border: '1px solid #EADAF0', borderRadius: '10px' }}
      >
        <Typography sx={tituloSx}>Fecha de validez</Typography>
        <Typography data-testid="validez-solo-lectura" sx={{ fontSize: 15, color: '#3B3542', mt: 0.5 }}>
          {valorMostrado ? `Válida hasta ${valorMostrado}` : 'Sin fecha de validez'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="validez-block"
      sx={{ mt: mtSuperior, p: 1.5, background: '#FBF7FC', border: '1px solid #EADAF0', borderRadius: '10px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography sx={tituloSx}>Fecha de validez</Typography>
        {guardando && (
          <Typography data-testid="validez-guardando" sx={{ fontSize: 12, color: '#9A8FA3' }}>
            Guardando…
          </Typography>
        )}
      </Box>

      <TextField
        type="date"
        size="small"
        label="Válida hasta"
        value={valorMostrado}
        onChange={(e) => {
          const v = e.target.value;
          if (v) onCambiar(v);
        }}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min, max, 'data-testid': 'validez-input' }}
        sx={{ width: { xs: '100%', sm: 260 } }}
      />

      {error && (
        <Typography data-testid="validez-error" sx={{ fontSize: 13, color: '#E03030', mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

export default ValidezBlock;
